import type { SimulationTest, GenerationMode, MessageInstance } from '@k-ucap/shared';
import { MessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { createGenerationContext, generateMessageInstance } from '@/domain/simulation/MessageGenerationService.js';
import { defaultRandom, SeededRandom, type RandomGenerator } from '@/domain/simulation/SeededRandom.js';

export type SchedulerRunStatus = 'IDLE' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'STOPPED' | 'ERROR';

export interface SchedulerRuleState {
  ruleId: string;
  messageType: number;
  generationMode: GenerationMode;
  intervalMs: number;
  startDelayMs: number;
  nextExecutionAt: number;
  remainingDelayMs: number;
  generatedCount: number;
  remainingCount: number | null;
  status: 'idle' | 'running' | 'paused' | 'done' | 'stopped';
  error?: string;
}

export interface SimulationSchedulerCallbacks {
  onMessage: (ruleId: string, message: MessageInstance, severity: 'PASS' | 'WARN' | 'FAIL') => void;
  onRuleStatus: (ruleId: string, status: SchedulerRuleState['status'], error?: string) => void;
  onElapsed: (elapsedMs: number) => void;
  onStatusChange: (status: SchedulerRunStatus) => void;
  onError: (error: string) => void;
}

export interface SimulationSchedulerConfig {
  test: SimulationTest;
  callbacks: SimulationSchedulerCallbacks;
  tickMs?: number;
  chunkSize?: number;
  seed?: string;
}

const DEFAULT_TICK_MS = 50;
const DEFAULT_CHUNK_SIZE = 100;
const MIN_INTERVAL_MS = 100;

export class SimulationScheduler {
  private readonly test: SimulationTest;
  private readonly callbacks: SimulationSchedulerCallbacks;
  private readonly tickMs: number;
  private readonly chunkSize: number;
  private readonly random: RandomGenerator;
  private readonly ruleStates: Record<string, SchedulerRuleState>;
  private readonly dictionaryService: MessageDictionaryService;

  private timer: ReturnType<typeof setInterval> | null = null;
  private elapsedMs = 0;
  private lastTickAt: number | null = null;
  private runStatus: SchedulerRunStatus = 'IDLE';

  constructor(config: SimulationSchedulerConfig) {
    this.test = config.test;
    this.callbacks = config.callbacks;
    this.tickMs = config.tickMs ?? DEFAULT_TICK_MS;
    this.chunkSize = config.chunkSize ?? DEFAULT_CHUNK_SIZE;
    this.random = config.seed ? new SeededRandom(config.seed) : defaultRandom;
    this.ruleStates = this.initializeRuleStates();
    this.dictionaryService = new MessageDictionaryService();
  }

  private initializeRuleStates(): Record<string, SchedulerRuleState> {
    const states: Record<string, SchedulerRuleState> = {};
    this.test.messageRules.forEach((rule) => {
      if (!rule.enabled) return;
      states[rule.id] = {
        ruleId: rule.id,
        messageType: rule.messageType,
        generationMode: rule.generationMode,
        intervalMs: Math.max(rule.intervalMs, MIN_INTERVAL_MS),
        startDelayMs: rule.startDelayMs,
        nextExecutionAt: 0,
        remainingDelayMs: rule.startDelayMs,
        generatedCount: 0,
        remainingCount: rule.count,
        status: 'idle',
      };
    });
    return states;
  }

  start(): void {
    if (this.runStatus === 'RUNNING') return;
    this.runStatus = 'RUNNING';
    this.lastTickAt = performance.now();
    this.setAllRuleStatus('running');
    this.callbacks.onStatusChange('RUNNING');
    this.timer = setInterval(() => this.tick(), this.tickMs);
  }

  pause(): void {
    if (this.runStatus !== 'RUNNING') return;
    this.clearTimer();
    this.runStatus = 'PAUSED';
    this.setAllRuleStatus('paused');
    this.callbacks.onStatusChange('PAUSED');
  }

  resume(): void {
    if (this.runStatus !== 'PAUSED') return;
    this.runStatus = 'RUNNING';
    this.setAllRuleStatus('running');
    this.lastTickAt = performance.now();
    this.callbacks.onStatusChange('RUNNING');
    this.timer = setInterval(() => this.tick(), this.tickMs);
  }

  stop(): void {
    this.clearTimer();
    this.runStatus = 'STOPPED';
    Object.values(this.ruleStates).forEach((state) => {
      if (state.status !== 'done') {
        state.status = 'stopped';
        this.callbacks.onRuleStatus(state.ruleId, 'stopped');
      }
    });
    this.callbacks.onStatusChange('STOPPED');
  }

  private clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private setAllRuleStatus(status: SchedulerRuleState['status']): void {
    Object.values(this.ruleStates).forEach((state) => {
      if (state.status !== 'done') {
        state.status = status;
        this.callbacks.onRuleStatus(state.ruleId, status);
      }
    });
  }

  private tick(): void {
    const now = performance.now();
    const deltaMs = this.lastTickAt ? now - this.lastTickAt : this.tickMs;
    this.lastTickAt = now;
    this.elapsedMs += deltaMs;

    Object.values(this.ruleStates).forEach((state) => {
      if (state.status === 'done') return;

      if (state.remainingDelayMs > 0) {
        state.remainingDelayMs -= deltaMs;
        if (state.remainingDelayMs <= 0) {
          state.nextExecutionAt = state.intervalMs;
          this.executeRule(state);
        }
        return;
      }

      state.nextExecutionAt -= deltaMs;
      if (state.nextExecutionAt <= 0) {
        this.executeRule(state);
        state.nextExecutionAt += state.intervalMs;
      }
    });

    const states = Object.values(this.ruleStates);
    const allDone = states.length > 0 && states.every((s) => s.status === 'done');

    this.callbacks.onElapsed(this.elapsedMs);

    if (allDone && this.runStatus === 'RUNNING') {
      this.completeRun();
    }
  }

  private executeRule(state: SchedulerRuleState): void {
    if (state.generationMode === 'SINGLE') {
      this.generateForRule(state, 1);
      state.status = 'done';
      this.callbacks.onRuleStatus(state.ruleId, 'done');
      return;
    }

    if (state.generationMode === 'BULK') {
      this.executeBulk(state);
      return;
    }

    // PERIODIC, RANDOM, ROUTE_BASED
    this.generateForRule(state, 1);
    if (state.remainingCount !== null) {
      state.remainingCount--;
      if (state.remainingCount <= 0) {
        state.status = 'done';
        this.callbacks.onRuleStatus(state.ruleId, 'done');
      }
    }
  }

  private executeBulk(state: SchedulerRuleState): void {
    const remaining = state.remainingCount ?? Infinity;
    const batchSize = Math.min(this.chunkSize, remaining === Infinity ? this.chunkSize : remaining);

    this.generateForRule(state, batchSize);

    if (state.remainingCount !== null) {
      state.remainingCount -= batchSize;
      if (state.remainingCount <= 0) {
        state.status = 'done';
        this.callbacks.onRuleStatus(state.ruleId, 'done');
      }
    }
  }

  private generateForRule(state: SchedulerRuleState, count: number): void {
    const rule = this.test.messageRules.find((r) => r.id === state.ruleId);
    if (!rule) return;

    const definition = this.dictionaryService.getMessageByType(rule.messageType);
    if (!definition) {
      state.status = 'done';
      state.error = `Message definition not found for type ${rule.messageType}`;
      this.callbacks.onRuleStatus(state.ruleId, 'done', state.error);
      this.callbacks.onError(state.error);
      return;
    }

    const defaultCoordinate = this.test.locationConfig?.defaultCoordinate ?? {
      mode: 'CENTER_RADIUS',
      centerLat: 37.389,
      centerLon: 126.647,
      radiusKm: 10,
    };

    for (let i = 0; i < count; i++) {
      state.generatedCount++;
      const context = createGenerationContext(state.generatedCount, defaultCoordinate, this.random);
      const message = generateMessageInstance(rule, definition, context);
      this.callbacks.onMessage(state.ruleId, message, 'PASS');
    }
  }

  private completeRun(): void {
    this.clearTimer();
    this.runStatus = 'COMPLETED';
    this.callbacks.onStatusChange('COMPLETED');
  }

  getRunStatus(): SchedulerRunStatus {
    return this.runStatus;
  }

  getRuleStates(): Record<string, SchedulerRuleState> {
    return { ...this.ruleStates };
  }

  getElapsedMs(): number {
    return this.elapsedMs;
  }
}

export const createSimulationScheduler = (config: SimulationSchedulerConfig): SimulationScheduler =>
  new SimulationScheduler(config);
